<template>
  <nav class="fixed !w-full z-50 h-[62px] bg-app-gray-900 border-app-gray-600 border-b px-4 lg:px-6 py-5 flex items-center">
    <div class="flex flex-wrap items-center justify-between w-full">
      <div class="flex flex-1 justify-start items-center">
        <img
          alt="MetaMask logo"
          :src="DEFAULT_WEB3AUTH_BY_METAMASK_LOGO"
          height="32"
          class="hidden xs:block cursor-pointer max-h-9 invert"
          aria-hidden
          @click="redirect"
        />
        <img
          alt="MetaMask icon"
          :src="DEFAULT_WEB3AUTH_LOGO_LIGHT_URL"
          class="block xs:hidden cursor-pointer max-h-6"
          aria-hidden
          @click="redirect"
        />
        <Icon
          name="menu-alt-two-solid-icon"
          class="cursor-pointer block xs:hidden lg:hidden ml-2 text-app-gray-100"
          size="24"
          @click="setMobileDrawer"
        />
      </div>
      <ul class="flex items-center gap-6">
        <li v-if="needUpgradePlan" class="relative">
          <Badge variant="info" class="!p-4 !text-xs cursor-pointer" @click="goToBilling">
            <Icon name="fire-solid-icon" size="small" class="mr-1 !w-5 !h-5"></Icon>
            <span class="text-sm">Upgrade</span>
          </Badge>
        </li>
        <li class="relative">
          <div
            v-if="unreadCount > 0"
            class="w-4 h-4 bg-app-red-500 rounded-full absolute -right-1 -top-2 flex items-center justify-center text-[8px] font-medium text-app-white p-1 box-border"
          >
            {{ unreadCount }}
          </div>
          <Popover position="bottom" is-dark :hide="showNotifications">
            <Icon name="bell-solid-icon" size="24" class="text-app-gray-200 cursor-pointer" @click="setNotificationDrawer" />
            <template #popover-content>
              <p class="px-3 py-2 text-app-white text-sm font-medium">Notification</p>
            </template>
          </Popover>
        </li>
        <li ref="helpModal" class="relative">
          <Popover position="bottom" is-dark :hide="showHelpModal">
            <Icon
              name="question-mark-circle-solid-icon"
              size="24"
              class="text-app-gray-200 cursor-pointer"
              @click="
                showHelpModal = !showHelpModal;
                showProfile = false;
                showNotifications = false;
              "
            />
            <template #popover-content>
              <p class="px-3 py-2 text-app-white text-sm font-medium w-max">Help Center</p>
            </template>
          </Popover>
          <TransitionPopup>
            <div
              v-show="showHelpModal"
              class="absolute top-[50px] -right-2 bg-app-gray-800 border border-app-gray-600 rounded-lg shadow-modal w-max p-4 flex flex-col"
            >
              <p class="text-xs font-semibold text-app-gray-200 uppercase tracking-wider">Resources</p>
              <Link
                href="https://builder.metamask.io/"
                target="_blank"
                rel="noopener noreferrer"
                class="!text-app-gray-100 !text-sm no-underline mt-2"
              >
                Builder Hub
              </Link>
              <router-link :to="{ name: 'getting-started' }" class="text-app-gray-100 !text-sm no-underline mt-2">Getting Started Guide</router-link>
              <p class="text-xs font-semibold text-app-gray-200 uppercase tracking-wider mt-6">Get In Touch</p>
              <Link
                href="https://form.jotform.com/232181978995071"
                target="_blank"
                rel="noopener noreferrer"
                class="!text-app-gray-100 !text-sm no-underline mt-2"
              >
                Give feedback
              </Link>
              <Link
                :href="config.enterprisePlanCalendlyLink"
                target="_blank"
                rel="noopener noreferrer"
                class="!text-app-gray-100 !text-sm no-underline mt-2"
              >
                Schedule demo
              </Link>
            </div>
          </TransitionPopup>
        </li>
        <li ref="profile" class="relative">
          <Popover position="bottom" is-dark :hide="showProfile">
            <Avatar
              aria-hidden
              @click.stop="
                showProfile = !showProfile;
                showHelpModal = false;
                showNotifications = false;
              "
            >
              {{ usernameInitials }}
            </Avatar>
            <template #popover-content>
              <p class="px-3 py-2 text-app-white text-sm font-medium">Profile</p>
            </template>
          </Popover>
          <TransitionPopup>
            <div v-show="showProfile" class="absolute top-14 right-0 bg-app-gray-800 border border-app-gray-600 rounded-lg shadow-modal w-56 z-40">
              <div class="p-3 border-b border-app-gray-600">
                <h2 class="text-sm font-bold text-app-white w-[90%] truncate">{{ userName }}</h2>
                <p class="text-sm font-normal text-app-gray-200 w-[90%] truncate">{{ userEmail }}</p>
              </div>
              <router-link :to="profileSettingLink" aria-label="link to profile settings">
                <div
                  class="p-3 text-sm font-normal text-app-gray-100 border-b border-app-gray-600 hover:bg-app-gray-700 hover:text-app-white transition-colors"
                  aria-hidden
                  @click="showProfile = false"
                >
                  Profile Settings
                </div>
              </router-link>
              <div class="p-3">
                <Link class="text-sm" @click="logout">Log Out</Link>
              </div>
            </div>
          </TransitionPopup>
        </li>
        <Icon
          :name="showMobileDrawer ? 'x-solid-icon' : 'menu-alt-two-solid-icon'"
          class="cursor-pointer hidden xs:block lg:hidden text-app-gray-200"
          size="24"
          @click="setMobileDrawer"
        />
      </ul>
    </div>
    <FeedbackModal :open="showFeedbackModal" @on-close="showFeedbackModal = false" />
    <Notification :open="showNotifications" @on-close="setNotificationDrawer" />
  </nav>
</template>

<script lang="ts">
import { Avatar } from "@toruslabs/vue-components/Avatar";
import { Badge } from "@toruslabs/vue-components/Badge";
import { Icon } from "@toruslabs/vue-components/Icon";
import { Link } from "@toruslabs/vue-components/Link";
import { defineComponent } from "vue";

import FeedbackModal from "@/components/FeedbackModal";
import Notification from "@/components/Home/Notification";
import Popover from "@/components/Popover";
import TransitionPopup from "@/components/TransitionPopup";
import useGettingStartedHidden from "@/composables/useGettingStartedHidden";
import config from "@/config";
import { billingModule, notificationModule, teamModule, userModule } from "@/store";
import { DEFAULT_WEB3AUTH_BY_METAMASK_LOGO, DEFAULT_WEB3AUTH_LOGO_LIGHT_URL, ROUTES, SUBSCRIPTION_PLAN, TEAM_PERMISSION } from "@/utils/enums";
import { nameInitials } from "@/utils/string";
import { hasPermission } from "@/utils/team";

export default defineComponent({
  name: "Navbar",
  components: { Avatar, Badge, FeedbackModal, Icon, Link, Notification, TransitionPopup, Popover },
  setup() {
    return {
      gettingStartedHidden: useGettingStartedHidden(),
      DEFAULT_WEB3AUTH_BY_METAMASK_LOGO,
      DEFAULT_WEB3AUTH_LOGO_LIGHT_URL,
      config,
    };
  },
  data() {
    return {
      showMenu: false,
      showProfile: false,
      isOpen: false,
      showHelpModal: false,
      showFeedbackModal: false,
      showHelpDialog: false,
      showNotifications: false,
    };
  },
  computed: {
    showMobileDrawer(): boolean {
      return userModule.showMobileDrawer;
    },
    userName(): string {
      return userModule.userProfileGetter?.name || "";
    },
    userEmail(): string {
      return userModule.userInfoGetter?.email || "";
    },
    usernameInitials(): string {
      return nameInitials(this.userName);
    },
    unreadCount() {
      return notificationModule.notifications.filter((item: { is_read: boolean }) => !item.is_read).length;
    },
    teamName(): string {
      return this.$route.params.teamName as string;
    },
    profileSettingLink() {
      return `/organization/${this.teamName}/profile-settings`;
    },
    selectedTeam() {
      return teamModule.selectedTeamGetter(this.teamName);
    },
    subscribedToPlan() {
      const { customer } = billingModule.getSubscription(this.selectedTeam.id);
      if (!customer) return true;
      const { plan } = customer;
      return !!plan && plan !== SUBSCRIPTION_PLAN.base;
    },
    needUpgradePlan() {
      return hasPermission(this.selectedTeam.role, TEAM_PERMISSION.billing) && !this.subscribedToPlan;
    },
  },
  watch: {
    showProfile(flag) {
      if (flag) {
        document.addEventListener("click", this.handleClickOutside);
      } else {
        document.removeEventListener("click", this.handleClickOutside);
      }
    },
    showHelpModal(flag) {
      if (flag) {
        document.addEventListener("click", this.handleClickOutside);
      } else {
        document.removeEventListener("click", this.handleClickOutside);
      }
    },
  },
  mounted() {
    this.setHelpDialog();
  },
  methods: {
    openPopover() {
      this.isOpen = true;
    },
    setMobileDrawer() {
      userModule.setMobileDrawer();
      this.showNotifications = false;
      this.showHelpModal = false;
      this.showProfile = false;
    },
    setHelpDialog() {
      this.showHelpDialog = true;
    },
    setNotificationDrawer() {
      this.showNotifications = !this.showNotifications;
      this.showHelpModal = false;
      this.showProfile = false;
    },
    async logout() {
      // Auth0 logout (social/passwordless) triggers a full-page redirect — return true.
      // Bridge/agentic wallet logout only clears local state — return false; navigate ourselves.
      const redirected = await userModule.logout();
      if (!redirected) {
        this.$router.push({ name: ROUTES.LOGIN });
      }
    },
    handleClickOutside(event: Event) {
      if (this.$refs.profile && !(this.$refs.profile as HTMLElement).contains(event.target as HTMLInputElement)) {
        this.showProfile = false;
      }
      if (this.$refs.helpModal && !(this.$refs.helpModal as HTMLElement).contains(event.target as HTMLInputElement)) {
        this.showHelpModal = false;
      }
    },
    goToBilling() {
      this.$router.push({ name: ROUTES.TEAM_BILLING, params: { teamName: this.teamName } });
    },
    redirect() {
      this.$router.push({ name: ROUTES.HOME, params: { teamName: this.teamName } });
    },
  },
});
</script>
